import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Button } from "@groot/ui/button";
import { Checkbox } from "@groot/ui/checkbox";
import { RecordPaymentDialog } from "@/components/RecordPaymentDialog";
import { useBillSummary } from "@/hooks/api/useBills";
import type { Bill } from "@/types";
import dayjs from "dayjs";
import { CheckCircle2, List, Plus, Receipt } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Helper to format due date in a subtle way
function formatDueDate(bill: Bill) {
  const nextDate = dayjs(bill.nextBillingDate);
  const today = dayjs().startOf("day");
  const daysUntil = nextDate.startOf("day").diff(today, "day");

  if (daysUntil < 0) {
    return { text: `${Math.abs(daysUntil)}d overdue`, isOverdue: true };
  }
  if (daysUntil === 0) {
    return { text: "Today", isOverdue: false };
  }
  if (daysUntil === 1) {
    return { text: "Tomorrow", isOverdue: false };
  }
  if (daysUntil <= 7) {
    return { text: `${daysUntil}d`, isOverdue: false };
  }
  return { text: nextDate.format("MMM D"), isOverdue: false };
}

export function BillsDashboardCard() {
  const navigate = useNavigate();
  const { data: summary, isLoading } = useBillSummary();
  const [billToMarkPaid, setBillToMarkPaid] = useState<Bill | null>(null);

  if (isLoading) {
    return (
      <div className="h-full border border-border rounded-xl overflow-hidden bg-background shadow-sm flex flex-col">
        <div className="border-b border-border bg-muted/50 px-6 py-4">
          <div className="flex items-center gap-2">
            <Receipt className="size-5 text-muted-foreground/70" />
            <h2 className="text-base font-medium text-foreground">Bills</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="flex h-24 items-center justify-center">
            <div className="size-5 animate-spin rounded-full border-2 border-border border-t-border" />
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const allUpcomingBills = [...summary.overdueBills, ...summary.upcomingBills].slice(0, 6);
  const hasNoBills = summary.totalActive === 0;

  return (
    <>
      <div className="h-full border border-border rounded-xl overflow-hidden bg-background shadow-sm flex flex-col">
        <div className="border-b border-border bg-muted/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="size-5 text-muted-foreground/70" />
              <h2 className="text-base font-medium text-foreground">Bills</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/bills")}
              className="text-muted-foreground/70 hover:text-muted-foreground -mr-2 size-8 p-0"
            >
              <List className="size-4" />
            </Button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {hasNoBills ? (
            <div className="p-6 text-center">
              <Receipt className="mx-auto size-8 text-muted-foreground/60" />
              <p className="mt-2 text-sm text-muted-foreground">No bills yet</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => navigate("/bills/new")}
              >
                <Plus className="size-3.5" />
                Add Bill
              </Button>
            </div>
          ) : allUpcomingBills.length > 0 ? (
            allUpcomingBills.map((bill) => {
              const dueInfo = formatDueDate(bill);
              return (
                <div
                  key={bill.id}
                  className="flex items-center gap-3 px-6 py-3 hover:bg-muted cursor-pointer group"
                  onClick={() => navigate(`/bills/${bill.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/bills/${bill.id}`);
                    }
                  }}
                  tabIndex={0}
                  // biome-ignore lint/a11y/useSemanticElements: using div for complex interactive element
                  role="button"
                >
                  <Checkbox
                    checked={false}
                    onCheckedChange={() => setBillToMarkPaid(bill)}
                    onClick={(e) => e.stopPropagation()}
                    className="size-4 rounded-full border-input data-[state=checked]:bg-success data-[state=checked]:border-success/50"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{bill.name}</p>
                  </div>
                  <span
                    className={`text-xs ${dueInfo.isOverdue ? "text-destructive font-medium" : "text-muted-foreground/70"}`}
                  >
                    {dueInfo.text}
                  </span>
                  <span className="text-sm text-foreground/80 tabular-nums">
                    <AmountDisplay amount={bill.amount} />
                  </span>
                </div>
              );
            })
          ) : (
            <div className="p-6 text-center">
              <CheckCircle2 className="mx-auto size-6 text-success" />
              <p className="mt-2 text-sm text-muted-foreground">All caught up!</p>
            </div>
          )}
        </div>
      </div>

      <RecordPaymentDialog
        bill={billToMarkPaid}
        open={billToMarkPaid !== null}
        onOpenChange={(open) => !open && setBillToMarkPaid(null)}
      />
    </>
  );
}
